import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable, tap} from 'rxjs';
import {ApiService} from './api.service';

export interface TimezoneSettings {
    timezone: string;
    selectedTimezone: string | null;
    prompted: boolean;
    kyivTimezone: string;
    today: string;
}
export interface TimezoneResponse { success: boolean; settings: TimezoneSettings; showPrompt?: boolean; }

@Injectable({providedIn: 'root'})
export class TimezoneService {
    readonly settings$ = new BehaviorSubject<TimezoneSettings | null>(null);
    normalize(zone: string): string { return zone === 'Europe/Kiev' ? 'Europe/Kyiv' : zone; }
    constructor(private api: ApiService) {}
    detected(): string {
        try { return this.normalize(Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Kyiv'); }
        catch { return 'Europe/Kyiv'; }
    }
    offset(zone: string, date = new Date()): string {
        try {
            const parts = new Intl.DateTimeFormat('en', {timeZone: zone, timeZoneName: 'longOffset'}).formatToParts(date);
            const value = parts.find(part => part.type === 'timeZoneName')?.value || 'GMT';
            return value === 'GMT' ? 'GMT +00:00' : value.replace('GMT', 'GMT ');
        } catch { return ''; }
    }
    city(zone: string): string { return (zone.split('/').pop() || zone).replace(/_/g, ' '); }
    accept(settings: TimezoneSettings | null): void { this.settings$.next(settings); }
    claim(studentId: number): Observable<TimezoneResponse> {
        return this.api.sendApiRequest('/parent/timezone/prompt', {studentId, timezone: this.detected()}, 'post') as Observable<TimezoneResponse>;
    }
    save(studentId: number, timezone: string): Observable<TimezoneResponse> {
        return (this.api.sendApiRequest('/parent/timezone', {studentId, timezone: this.normalize(timezone)}, 'put') as Observable<TimezoneResponse>)
            .pipe(tap(response => { if (response.success) this.accept(response.settings); }));
    }
    zones(): string[] {
        const intl = Intl as typeof Intl & {supportedValuesOf?: (key: string) => string[]};
        const fallback = ['Europe/London','Europe/Warsaw','Europe/Berlin','Europe/Paris','Europe/Lisbon',
            'Europe/Istanbul','America/New_York','America/Toronto','America/Chicago','America/Los_Angeles',
            'America/St_Johns','Asia/Dubai','Asia/Kolkata','Asia/Kathmandu','Asia/Colombo','Asia/Yangon',
            'Asia/Bangkok','Asia/Tokyo','Australia/Adelaide','Australia/Sydney','Pacific/Auckland','UTC'];
        let supported = fallback;
        try { supported = intl.supportedValuesOf?.('timeZone') || fallback; } catch { /* Older WebViews use fallback. */ }
        return [...new Set(['Europe/Kyiv', this.detected(), this.settings$.value?.timezone || 'Europe/Kyiv', ...supported]
            .map(zone => this.normalize(zone)))];
    }
}
